import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  FileText, StickyNote, Plus, Search, Copy, Check, Clock, ChevronRight,
  ChevronLeft, Loader2, Pencil, Trash2, X, Ticket,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  fetchNotes, createSnippet, updateSnippet, deleteSnippet,
  createNote, updateNote, deleteNote, fetchLinkableTickets,
} from '@/hooks/useNotes';
import type { NoteSnippet, PrivateNote, FavoriteTicketOption } from '@uniticket/shared';

// Onglet « Notes » — refonte UX du 2026-09-10.
//
// LE PRINCIPE, ET IL EXPLIQUE TOUT LE RESTE : **aperçu compact d'abord, contenu complet seulement
// quand on le demande.** La version précédente posait deux grandes zones de texte à même la page ;
// passé trois entrées, elle devenait un mur qu'on faisait défiler. Ici : des cartes, et une modale.
//
// Recherche, tri et pagination sont CÔTÉ NAVIGATEUR (décision du user, 10-09) : tout est chargé en une
// fois, donc la recherche est instantanée et ne coûte aucun appel. Ça tient très largement jusqu'à
// quelques centaines d'entrées — l'ordre de grandeur réaliste ici. Au-delà, il faudra paginer au
// serveur, et c'est `fetchNotes` qu'il faudra changer, pas cet écran.

type Tab = 'shared' | 'mine';
type SortKey = 'recent' | 'oldest' | 'az' | 'za';
const PAGE_SIZE = 6;

/** Une entrée, quelle que soit sa bibliothèque : les deux onglets partagent la même mise en page,
 *  seules les actions diffèrent. */
interface Entry {
  id: string; title: string; body: string;
  updatedAt?: string | null; updatedBy?: string | null;
  /** Notes privées seulement : le ticket rattaché. Les libellés sont FIGÉS au rattachement, donc ils
   *  restent lisibles quand l'étoile a été retirée depuis. */
  ticketId?: string | null; ticketNumber?: string | null; ticketSubject?: string | null;
}

export function NotesPage() {
  const { t } = useTranslation(['tickets', 'common']);
  const [loading, setLoading] = useState(true);
  // « Mes notes privées » à l'ouverture, pas « Réponses partagées » (demande user 2026-09-14) : les
  // extraits partagés se consultent en passant, depuis la fenêtre de validation qui les insère ; venir
  // SUR cette page, c'est venir écrire ou relire ses propres notes.
  const [tab, setTab] = useState<Tab>('mine');
  const [canEdit, setCanEdit] = useState(false);
  const [snippets, setSnippets] = useState<NoteSnippet[]>([]);
  const [notes, setNotes] = useState<PrivateNote[]>([]);

  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('recent');
  const [page, setPage] = useState(1);
  const searchRef = useRef<HTMLInputElement>(null);

  const [reading, setReading] = useState<Entry | null>(null);
  const [editing, setEditing] = useState<{ entry: Entry | null } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Entry | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    const res = await fetchNotes();
    setSnippets(res.data.snippets);
    setNotes(res.data.notes);
    setCanEdit(res.data.canEditSnippets);
  }

  useEffect(() => {
    let alive = true;
    fetchNotes()
      .then((res) => {
        if (!alive) return;
        setSnippets(res.data.snippets);
        setNotes(res.data.notes);
        setCanEdit(res.data.canEditSnippets);
      })
      .catch((err) => toast.error(err?.message || t('tickets:notes.loadError')))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [t]);

  // Changer d'onglet remet la recherche et la page à zéro : garder « proximus » en passant sur les
  // notes privées afficherait une liste vide qu'on lirait comme « je n'ai aucune note ».
  useEffect(() => { setQ(''); setPage(1); }, [tab]);
  useEffect(() => { setPage(1); }, [q, sort]);
  useEffect(() => { searchRef.current?.focus(); }, [tab]);

  const entries: Entry[] = tab === 'shared' ? snippets : notes;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    // La recherche porte sur le TITRE ET LE CONTENU : on se souvient plus souvent d'une phrase de la
    // réponse que de son titre.
    const base = needle
      ? entries.filter((e) => e.title.toLowerCase().includes(needle) || e.body.toLowerCase().includes(needle))
      : entries;
    const byDate = (e: Entry) => (e.updatedAt ? Date.parse(e.updatedAt) : 0);
    const copy = [...base];
    switch (sort) {
      case 'recent': copy.sort((a, b) => byDate(b) - byDate(a)); break;
      case 'oldest': copy.sort((a, b) => byDate(a) - byDate(b)); break;
      case 'az': copy.sort((a, b) => a.title.localeCompare(b.title)); break;
      case 'za': copy.sort((a, b) => b.title.localeCompare(a.title)); break;
    }
    return copy;
  }, [entries, q, sort]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const shown = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const canWrite = tab === 'mine' || canEdit;

  const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');
  const preview = (s: string) => (s.length > 140 ? `${s.slice(0, 140)}…` : s);

  async function copy(e: Entry) {
    try {
      await navigator.clipboard.writeText(e.body);
      setCopied(e.id);
      window.setTimeout(() => setCopied((c) => (c === e.id ? null : c)), 1600);
      toast.success(t('tickets:notes.copiedToast'));
    } catch {
      toast.error(t('tickets:notes.copyError'));
    }
  }

  async function save(title: string, body: string, entry: Entry | null, ticketId: string | null) {
    setBusy(true);
    try {
      if (tab === 'shared') {
        if (entry) await updateSnippet(entry.id, title, body);
        else await createSnippet(title, body);
      } else {
        if (entry) await updateNote(entry.id, title, body, ticketId);
        else await createNote(title, body, ticketId);
      }
      await reload();
      setEditing(null);
      setReading(null);
      toast.success(t(entry ? 'tickets:notes.saved' : 'tickets:notes.created'));
    } catch (err: any) {
      toast.error(err?.message || t('tickets:notes.saveError'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(entry: Entry) {
    setBusy(true);
    try {
      if (tab === 'shared') await deleteSnippet(entry.id);
      else await deleteNote(entry.id);
      await reload();
      setConfirmDelete(null);
      setReading(null);
      toast.success(t('tickets:notes.deleted'));
    } catch (err: any) {
      toast.error(err?.message || t('tickets:notes.deleteError'));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('tickets:notes.loading')}
      </div>
    );
  }

  const Icon = tab === 'shared' ? FileText : StickyNote;

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-xl font-bold text-slate-900">{t('tickets:notes.pageTitle')}</h1>
      <p className="mt-1 text-sm text-slate-500">{t('tickets:notes.pageSubtitle')}</p>

      {/* Onglets */}
      <div className="mt-5 flex items-center gap-1 border-b border-slate-200">
        <TabButton active={tab === 'shared'} onClick={() => setTab('shared')}>
          {t('tickets:notes.tabShared')}
          <span className="ml-2 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            {t('tickets:notes.adminBadge')}
          </span>
        </TabButton>
        <TabButton active={tab === 'mine'} onClick={() => setTab('mine')}>
          {t('tickets:notes.tabMine')}
        </TabButton>
      </div>

      {/* Barre d'outils */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t(tab === 'shared' ? 'tickets:notes.searchShared' : 'tickets:notes.searchMine')}
            className="pl-9"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          aria-label={t('tickets:notes.sortAria')}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 cursor-pointer"
        >
          <option value="recent">{t('tickets:notes.sortRecent')}</option>
          <option value="oldest">{t('tickets:notes.sortOldest')}</option>
          <option value="az">{t('tickets:notes.sortAz')}</option>
          <option value="za">{t('tickets:notes.sortZa')}</option>
        </select>
        {canWrite && (
          <Button onClick={() => setEditing({ entry: null })}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t(tab === 'shared' ? 'tickets:notes.newShared' : 'tickets:notes.newMine')}
          </Button>
        )}
      </div>

      {/* Grille */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={<Icon className="h-8 w-8 text-slate-300" />}
          title={q ? t('tickets:notes.emptySearchTitle') : t(tab === 'shared' ? 'tickets:notes.emptySharedTitle' : 'tickets:notes.emptyMineTitle')}
          hint={q ? t('tickets:notes.emptySearchHint') : t(tab === 'shared' ? 'tickets:notes.emptySharedHint' : 'tickets:notes.emptyMineHint')}
          action={!q && canWrite ? (
            <Button onClick={() => setEditing({ entry: null })}>
              <Plus className="mr-1.5 h-4 w-4" />
              {t(tab === 'shared' ? 'tickets:notes.newShared' : 'tickets:notes.newMine')}
            </Button>
          ) : null}
        />
      ) : (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {shown.map((e) => (
              <article
                key={e.id}
                onClick={() => setReading(e)}
                className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:border-slate-300 hover:shadow-sm"
              >
                <div className="flex items-start gap-2">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand-rose" />
                  <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">{e.title}</h3>
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-brand-rose" />
                </div>
                <p className="mt-2 line-clamp-2 whitespace-pre-line text-xs text-slate-500">{preview(e.body) || '—'}</p>
                {/* Le ticket rattaché, cliquable — `stopPropagation` parce que la carte entière ouvre
                    la modale. Les libellés viennent de l'instantané, donc ils restent lisibles même si
                    l'étoile a été retirée depuis. */}
                {e.ticketNumber && (
                  <Link
                    to={e.ticketId ? `/tickets/${e.ticketId}/address-validation` : '#'}
                    onClick={(ev) => ev.stopPropagation()}
                    className="mt-2 inline-flex max-w-full items-center gap-1 rounded bg-brand-rose/[0.08] px-1.5 py-0.5 text-[11px] font-semibold text-brand-rose hover:underline"
                  >
                    <Ticket className="h-3 w-3 shrink-0" />
                    <span className="truncate">{e.ticketNumber}{e.ticketSubject ? ` — ${e.ticketSubject}` : ''}</span>
                  </Link>
                )}
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                    <Clock className="h-3 w-3" />
                    {t('tickets:notes.modifiedOn', { when: fmt(e.updatedAt) })}
                    {tab === 'shared' && e.updatedBy ? ` · ${e.updatedBy}` : ''}
                  </span>
                  {/* Copier est sur la CARTE, pas seulement dans la modale : copier une réponse type
                      doit coûter UN clic — c'est le geste le plus fréquent de cet écran. */}
                  <Button
                    variant="outline" size="sm"
                    onClick={(ev) => { ev.stopPropagation(); void copy(e); }}
                  >
                    {copied === e.id
                      ? <><Check className="mr-1 h-3.5 w-3.5 text-emerald-600" />{t('tickets:notes.copied')}</>
                      : <><Copy className="mr-1 h-3.5 w-3.5" />{t('tickets:notes.copy')}</>}
                  </Button>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
            <span>
              {t('tickets:notes.counter', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, filtered.length),
                total: filtered.length,
              })}
            </span>
            {pages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button" disabled={current <= 1} onClick={() => setPage(current - 1)}
                  className="rounded p-1 disabled:opacity-30 hover:bg-slate-100"
                  aria-label={t('tickets:notes.prevPage')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p} type="button" onClick={() => setPage(p)}
                    className={cn('min-w-[26px] rounded px-1.5 py-0.5 font-semibold',
                      p === current ? 'bg-brand-rose text-white' : 'hover:bg-slate-100')}
                  >
                    {p}
                  </button>
                ))}
                <button
                  type="button" disabled={current >= pages} onClick={() => setPage(current + 1)}
                  className="rounded p-1 disabled:opacity-30 hover:bg-slate-100"
                  aria-label={t('tickets:notes.nextPage')}
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {reading && (
        <Modal onClose={() => setReading(null)} title={reading.title} icon={<Icon className="h-4 w-4 text-brand-rose" />}>
          {reading.ticketNumber && (
            <Link
              to={reading.ticketId ? `/tickets/${reading.ticketId}/address-validation` : '#'}
              className="mb-3 inline-flex items-center gap-1 rounded bg-brand-rose/[0.08] px-2 py-1 text-xs font-semibold text-brand-rose hover:underline"
            >
              <Ticket className="h-3.5 w-3.5" />
              {reading.ticketNumber}{reading.ticketSubject ? ` — ${reading.ticketSubject}` : ''}
            </Link>
          )}
          <p className="whitespace-pre-line text-sm text-slate-700">{reading.body || '—'}</p>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs text-slate-400">
              {t('tickets:notes.modifiedOn', { when: fmt(reading.updatedAt) })}
              {tab === 'shared' && reading.updatedBy ? ` · ${reading.updatedBy}` : ''}
            </span>
            <div className="flex items-center gap-2">
              {canWrite && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(reading)}>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5 text-red-600" />{t('tickets:notes.delete')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditing({ entry: reading })}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />{t('tickets:notes.edit')}
                  </Button>
                </>
              )}
              <Button onClick={() => void copy(reading)}>
                {copied === reading.id
                  ? <><Check className="mr-1.5 h-4 w-4" />{t('tickets:notes.copied')}</>
                  : <><Copy className="mr-1.5 h-4 w-4" />{t('tickets:notes.copy')}</>}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {editing && (
        <EditModal
          entry={editing.entry}
          busy={busy}
          shared={tab === 'shared'}
          onCancel={() => setEditing(null)}
          onSave={(title, body, ticketId) => void save(title, body, editing.entry, ticketId)}
        />
      )}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(null)} title={t('tickets:notes.deleteTitle')}>
          <p className="text-sm text-slate-600">{t('tickets:notes.deleteBody', { title: confirmDelete.title })}</p>
          <p className="mt-1 text-xs font-semibold text-red-700">{t('tickets:notes.deleteIrreversible')}</p>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>{t('tickets:notes.cancel')}</Button>
            <Button disabled={busy} onClick={() => void remove(confirmDelete)}>
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
              {t('tickets:notes.delete')}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button" onClick={onClick}
      className={cn(
        'inline-flex items-center border-b-2 px-3 py-2 text-sm font-semibold transition-colors',
        active ? 'border-brand-rose text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}

function EmptyState({ icon, title, hint, action }: {
  icon: React.ReactNode; title: string; hint: string; action?: React.ReactNode;
}) {
  return (
    <div className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-slate-200 bg-white/50 px-6 py-12 text-center">
      {icon}
      <p className="mt-3 text-sm font-semibold text-slate-700">{title}</p>
      <p className="mt-1 max-w-sm text-xs text-slate-500">{hint}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Modale centrée. `max-w` généreux et `w-[92vw]` : sur mobile elle prend presque toute la largeur,
 *  comme la spec le demande. */
function Modal({ title, icon, onClose, children }: {
  title: string; icon?: React.ReactNode; onClose: () => void; children: React.ReactNode;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4" onClick={onClose}>
      <div
        role="dialog" aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] w-[92vw] max-w-2xl overflow-auto rounded-xl bg-white p-5 shadow-xl"
      >
        <div className="mb-3 flex items-start gap-2 border-b border-slate-100 pb-3">
          {icon}
          <h2 className="min-w-0 flex-1 text-sm font-bold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="rounded p-0.5 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EditModal({ entry, shared, busy, onCancel, onSave }: {
  entry: Entry | null; shared: boolean; busy: boolean;
  onCancel: () => void; onSave: (title: string, body: string, ticketId: string | null) => void;
}) {
  const { t } = useTranslation(['tickets', 'common']);
  const [title, setTitle] = useState(entry?.title ?? '');
  const [body, setBody] = useState(entry?.body ?? '');
  const [ticketId, setTicketId] = useState<string | null>(entry?.ticketId ?? null);
  const [tickets, setTickets] = useState<FavoriteTicketOption[] | null>(null);

  // Les tickets rattachables sont SES FAVORIS. Chargés à l'ouverture de la modale, pas avec la page :
  // la plupart des notes ne sont rattachées à rien, et l'appel serait payé pour rien.
  useEffect(() => {
    if (shared) return;
    let alive = true;
    fetchLinkableTickets()
      .then((r) => { if (alive) setTickets(r.data); })
      .catch(() => { if (alive) setTickets([]); });
    return () => { alive = false; };
  }, [shared]);

  // Un ticket rattaché AVANT d'être retiré des favoris n'est plus dans la liste, et le menu l'aurait
  // silencieusement effacé au premier enregistrement. On le réinjecte, avec ses libellés figés.
  const options = useMemo(() => {
    const base = tickets ?? [];
    if (!entry?.ticketId || base.some((o) => o.ticketId === entry.ticketId)) return base;
    return [{ ticketId: entry.ticketId, ticketNumber: entry.ticketNumber ?? '—', subject: entry.ticketSubject ?? null }, ...base];
  }, [tickets, entry]);
  // Le titre est obligatoire (spec), et le serveur le refuse aussi : le bouton désactivé évite un
  // aller-retour qui n'apprendrait rien à l'utilisateur.
  const ok = title.trim().length > 0;
  return (
    <Modal
      onClose={onCancel}
      title={t(entry ? 'tickets:notes.editTitle' : shared ? 'tickets:notes.newShared' : 'tickets:notes.newMine')}
    >
      <label className="text-xs font-semibold text-slate-600">{t('tickets:notes.fieldTitle')}</label>
      <Input
        autoFocus value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
        placeholder={t(shared ? 'tickets:notes.titlePlaceholder' : 'tickets:notes.titlePlaceholderMine')}
        className="mt-1"
      />
      <label className="mt-3 block text-xs font-semibold text-slate-600">{t('tickets:notes.fieldBody')}</label>
      <textarea
        value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000}
        placeholder={t(shared ? 'tickets:notes.bodyPlaceholder' : 'tickets:notes.bodyPlaceholderMine')}
        className="mt-1 w-full min-h-[200px] rounded-md border border-slate-200 p-3 text-sm"
      />
      {/* Rattachement à un ticket — notes privées seulement. Le menu ne propose QUE les favoris : c'est
          la demande, et le serveur le revérifie (un ticket hors favoris est refusé en 400). */}
      {!shared && (
        <>
          <label className="mt-3 block text-xs font-semibold text-slate-600">{t('tickets:notes.fieldTicket')}</label>
          <select
            value={ticketId ?? ''}
            onChange={(e) => setTicketId(e.target.value || null)}
            className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
          >
            <option value="">{t('tickets:notes.noTicket')}</option>
            {options.map((o) => (
              <option key={o.ticketId} value={o.ticketId}>
                {o.ticketNumber}{o.subject ? ` — ${o.subject}` : ''}
              </option>
            ))}
          </select>
          {tickets != null && tickets.length === 0 && (
            <p className="mt-1 text-[11px] text-slate-400">{t('tickets:notes.noFavoritesHint')}</p>
          )}
        </>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>{t('tickets:notes.cancel')}</Button>
        <Button disabled={!ok || busy} onClick={() => onSave(title.trim(), body, shared ? null : ticketId)}>
          {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          {t('tickets:notes.save')}
        </Button>
      </div>
    </Modal>
  );
}

export default NotesPage;
